import { useState } from 'react';

export interface FormData {
	packageOfInterest: string; // Package of Interest
	travelStyle: string; // Travel Style & Accommodation (Luxury Stays, Budget Stays, etc.)
	interests: string[]; // Preferred Activities
	duration: number;
	groupSize: number; // Number of Guests
	childrenAges?: string; // Kids / Children ages
	name: string; // Full Name
	email: string; // Email Address
	phone: string; // Phone Number
	residence: string; // Where Do You Live?
	departureAirport: string; // Preferred Departure Airport
	wantFlights: boolean; // Flight suggestions
	dateFrom: string; // Date From
	dateTo: string; // Date To
	budgetExcludingFlights: string; // Budget excluding flights
	groupType: string; // Group Type category
	transportPreference: string; // Transport Preference
	message: string; // Special Requests
	website?: string; // Honeypot anti-spam field
}

export interface ContactFormData {
	name: string;
	email: string;
	phone: string;
	message: string;
	website?: string; // Honeypot anti-spam field
}

export interface GroupTripWaitlistData {
	email: string;
	trip?: string;
	language?: string;
	website?: string; // Honeypot anti-spam field
}

export interface UseEmailServiceResult {
	loading: boolean;
	status: 'idle' | 'sending' | 'success' | 'error';
	error: string | null;
	sendItineraryProposal: (
		data: FormData,
		reference: string,
		totalEstimate: number,
	) => Promise<{
		success: boolean;
		message: string;
		formattedHtml?: string;
	}>;
	sendContactInquiry: (data: ContactFormData) => Promise<{
		success: boolean;
		message: string;
		formattedHtml?: string;
	}>;
	sendGroupTripWaitlist: (data: GroupTripWaitlistData) => Promise<{
		success: boolean;
		message: string;
		formattedHtml?: string;
	}>;
}

const endpointUrl = (import.meta as any).env?.VITE_EMAIL_SERVICE_API_URL ?? '/api/Contact';

export function useEmailService(): UseEmailServiceResult {
	const [loading, setLoading] = useState(false);
	const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle');
	const [error, setError] = useState<string | null>(null);

	const sendItineraryProposal = async (data: FormData, reference: string, totalEstimate: number) => {
		setLoading(true);
		setStatus('sending');
		setError(null);

		// Honeypot anti-spam check
		if (data.website && data.website.trim()) {
			setStatus('success');
			setLoading(false);
			return {
				success: true,
				message: 'Thank you, our safari experts will contact you shortly',
			};
		}

		// 1. Map readable labels for packages of interest
		const packageMap: Record<string, string> = {
			'kruger-experience-big-5': 'Kruger Experience – Big 5 Safari (Kruger National Park)',
			'beach-to-big-5-safari': 'From the Beach to the Big 5 – Safari (KwaZulu-Natal)',
			'big-5-mountain-drakensberg': 'Big 5 Safari and Mountain Escapes in the Drakensberg (KwaZulu-Natal)',
			'cape-town-big-5-safari': 'Cape Town and Big 5 Safari (Western Cape, Kruger National Park, KwaZulu-Natal)',
			'full-experience-cape-village-beach-safari':
				'The Full Experience - Cape Town, Traditional Village, Beach, Safari and Big 5 (Western Cape and KwaZulu-Natal)',
			'ocean-expeditions-private-beach-lodge': 'Ocean Expeditions and Private Beach Lodge (KwaZulu-Natal)',
			'tailor-made-custom': 'Bespoke / Tailor-Made Itinerary',
		};

		// 2. Map readable labels for travel styles / accommodation
		const travelStylesMap: Record<string, string> = {
			'luxury-stays': 'Luxury stays (Upscale lodges & private reserves)',
			'budget-stays': 'Budget stays (Charming lodges & guesthouses)',
			'self-drive': 'Self-drive tours (Autonomy & rental routes)',
			big5: 'Big 5 safari tours (Predator & Big 5 focus)',
			'personalised-packages': 'Personalised packages (Tailored activities & flights)',
			'sustainable-interactions': 'Sustainable Interactions & Conservation',
			luxury: 'Luxury stays',
			budget: 'Budget stays',
			'big-5': 'Big 5 safari tours',
			personalised: 'Personalised packages',
			'sustainable-conservation': 'Sustainable Interactions and Conservation',
		};

		// 3. Map readable labels for interests / activities
		const interestsMap: Record<string, string> = {
			big5: 'Big 5 Spotting',
			photography: 'Wildlife Photography & Aerial Photocoaching',
			scenic: 'Scenic Flights',
			culinary: 'Culinary Experiences',
			conservation: 'Sustainable Conservation experiences & Eco-Research',
		};

		const packageLabel = packageMap[data.packageOfInterest] || data.packageOfInterest || 'Custom Package';
		const travelStyleLabel = travelStylesMap[data.travelStyle] || data.travelStyle || 'Luxury stays';
		const selectedInterests = data.interests.map(id => interestsMap[id] || id).join(', ');

		// 4. Construct clean, structured HTML designed specifically for email readers
		const htmlEmailTemplate = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Elephant Expeditions - Bespoke Proposal Request</title>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background-color: #f7f5f0; margin: 0; padding: 40px 20px;">
  <div style="max-w: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(15, 44, 36, 0.08); border: 1px solid #e3decb;">
    <!-- Brand Header Banner -->
    <div style="background-color: #0f2c24; padding: 32px; text-align: center; border-bottom: 3px solid #cda250;">
      <h1 style="color: #ffffff; font-family: 'Times New Roman', Georgia, serif; font-size: 26px; tracking: 0.15em; margin: 0; font-weight: normal; text-transform: uppercase;">ELEPHANT EXPEDITIONS</h1>
      <p style="color: #cda250; margin: 6px 0 0 0; font-size: 11px; font-weight: bold; letter-spacing: 0.25em; text-transform: uppercase;">Bespoke Planner Proposal</p>
    </div>

    <!-- Content Card -->
    <div style="padding: 36px; color: #2d2a23;">
      <div style="display: flex; justify-content: space-between; align-items: baseline; border-bottom: 1px solid #eae5db; padding-bottom: 12px; margin-bottom: 24px;">
        <span style="font-size: 11px; font-weight: bold; color: #0f2c24; text-transform: uppercase; letter-spacing: 1px; align-self: center;">Booking Code: </span>
        <span style="font-family: monospace; font-size: 14px; font-weight: bold; color: #b16a49; margin-left: 4px;">${reference}</span>
      </div>

      <p style="font-size: 14px; line-height: 1.6; color: #524f46; margin-bottom: 24px;">
        A new bespoke proposal request has been submitted via the Itinerary Builder wizard. A dedicated safari specialist will review these preferences and prepare a customized proposal.
      </p>

      <h3 style="font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: 1.5px; color: #0f2c24; margin: 0 0 12px 0; border-left: 2px solid #cda250; padding-left: 10px;">
        1. Core Travel Parameters
      </h3>
      
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 28px; font-size: 13px;">
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c; width: 40%;">Package of Interest:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${packageLabel}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Stay & Travel Style:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${travelStyleLabel}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Preferred Activities:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${selectedInterests || 'None chosen'}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Guest Attendance Count:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.groupSize} PAX</td>
        </tr>
        ${
					data.childrenAges && data.childrenAges.trim()
						? `
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Children / Kids Ages:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #b16a49;">${data.childrenAges}</td>
        </tr>`
						: ''
				}
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Date From:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.dateFrom || 'N/A'}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Date To:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.dateTo || 'N/A'}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Planned Duration:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.duration} nights</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Excluding Flight Budget:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">CHF ${parseFloat(data.budgetExcludingFlights.replace(/[^0-9.]/g, '') || '0').toLocaleString()}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Group Type Category:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.groupType}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Transport Style Pref:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.transportPreference}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Preferred Departure Airport:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.departureAirport || 'Not specified'}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Wants Flight Suggestions:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.wantFlights ? 'Yes' : 'No'}</td>
        </tr>
        <tr style="background-color: #0f2c24; color: #ffffff;">
          <td style="padding: 12px 14px; font-weight: bold;">Formulated Blueprint Base:</td>
          <td style="padding: 12px 14px; font-weight: bold; text-align: right; color: #cda250; font-size: 16px;"></td>
        </tr>
      </table>

      <h3 style="font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: 1.5px; color: #0f2c24; margin: 0 0 12px 0; border-left: 2px solid #cda250; padding-left: 10px;">
        2. Client Credentials
      </h3>
      
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 28px; font-size: 13px;">
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c; width: 40%;">Lead Explorer Name:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.name}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Private Contact Email:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;"><a href="mailto:${data.email}" style="color: #b16a49; text-decoration: none;">${data.email}</a></td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Contact Phone Number:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.phone}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Where they live (City, Country):</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.residence || 'Not supplied'}</td>
        </tr>
      </table>

      <h3 style="font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: 1.5px; color: #0f2c24; margin: 0 0 10px 0; border-left: 2px solid #cda250; padding-left: 10px;">
        3. Specialized Commentary & Special Requests
      </h3>
      <div style="background-color: #f7f5f0; border-radius: 8px; padding: 16px; font-size: 12.5px; line-height: 1.6; color: #524f46; border: 1px solid #eae5db; font-style: italic;">
        "${data.message || 'No additional custom parameters submitted.'}"
      </div>
    </div>

    <!-- Eco Footer Disclaimer -->
    <div style="background-color: #fcfbf9; padding: 24px; text-align: center; border-top: 1px solid #eae5db; font-size: 11px; color: #817a6c;">
      <p style="margin: 0 0 6px 0;">This transmission is reserved for validated ecological trust patrons.</p>
      <p style="margin: 0; color: #cda250; font-weight: bold;">© ${new Date().getFullYear()} Elephant Expeditions. Sovereign Concessions Register.</p>
    </div>
  </div>
</body>
</html>
    `;

		const plainTextBody = `
========================================
NEW BESPOKE PROPOSAL REQUEST - ${reference}
========================================
LEAD EXPLORER: ${data.name}
EMAIL: ${data.email}
PHONE: ${data.phone}
RESIDENCE: ${data.residence}
PROPOSAL TARGET: ${travelStyleLabel}

--- CHRONOLOGY & DEPARTURE ---
AIRPORT OF DEPARTURE: ${data.departureAirport || 'Not specified'}
DATE FROM: ${data.dateFrom}
DATE TO: ${data.dateTo}
DURATION CALCULATED: ${data.duration} Nights
WANTS FLIGHT RECOMMENDATIONS: ${data.wantFlights ? 'Yes' : 'No'}

--- FINANCES & TYPE ---
BUDGET LIMIT EXCLUDING FLIGHTS: CHF ${data.budgetExcludingFlights}
TYPE OF CLIENT GROUP: ${data.groupType}
TRANSPORT PREFERENCE: ${data.transportPreference}
ATTENDANCE QUANTITY: ${data.groupSize} PAX
CHILDREN / KIDS AGES: ${data.childrenAges && data.childrenAges.trim() ? data.childrenAges : 'None / Adults only'}

ACTIVITIES DESIRED: ${selectedInterests || 'None selected'}
ESTIMATED PROPOSAL BASE: CHF ${totalEstimate.toLocaleString()}

CLIENT SPECIAL MESSAGE:
"${data.message || 'No custom parameters submitted.'}"

========================================
Elephant Expeditions © ${new Date().getFullYear()}
    `;

		// 4. Try sending the email to an API URL if configured, otherwise fallback to an elegant simulation

		try {
			if (endpointUrl) {
				// Run active web transmission
				const response = await fetch(endpointUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						// reference,
						leadName: data.name,
						leadEmail: data.email,
						subject: `[Bespoke Proposal] ${data.name} - ${reference}`,
						html: htmlEmailTemplate,
						text: plainTextBody,
						// details: {
						// 	travelStyle: travelStyleLabel,
						// 	interests: data.interests,
						// 	duration: data.duration,
						// 	groupSize: data.groupSize,
						// 	priceEstimate: totalEstimate,
						// 	rawMessage: data.message,
						// },
					}),
				});

				if (!response.ok) {
					throw new Error(`Server returned error status ${response.status}`);
				}

				setStatus('success');
				setLoading(false);
				return {
					success: true,
					message: 'Thank you, our safari experts will contact you shortly',
					formattedHtml: htmlEmailTemplate,
				};
			} else {
				// Graceful simulate route transmission delay for immediate feedback
				await new Promise(resolve => setTimeout(resolve, 1800));

				setStatus('success');
				setLoading(false);
				return {
					success: true,
					message: 'Thank you, our safari experts will contact you shortly',
					formattedHtml: htmlEmailTemplate,
				};
			}
		} catch (err: any) {
			console.error('Email transmission failure:', err);
			setError(err?.message || 'Standard network gateway error. Please try again.');
			setStatus('error');
			setLoading(false);
			return {
				success: false,
				message: 'Could not transmit proposal. Please try again or contact concierge@elephantexpeditions.com directly.',
			};
		}
	};

	const sendContactInquiry = async (data: ContactFormData) => {
		setLoading(true);
		setStatus('sending');
		setError(null);

		// Honeypot anti-spam check
		if (data.website && data.website.trim()) {
			setStatus('success');
			setLoading(false);
			return {
				success: true,
				message: 'Thank you. Your message has been transmitted successfully.',
			};
		}

		const htmlEmailTemplate = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Elephant Expeditions - Contact Inquiry</title>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background-color: #f7f5f0; margin: 0; padding: 40px 20px;">
  <div style="max-w: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(15, 44, 36, 0.08); border: 1px solid #e3decb;">
    <!-- Brand Header Banner -->
    <div style="background-color: #0f2c24; padding: 32px; text-align: center; border-bottom: 3px solid #cda250;">
      <h1 style="color: #ffffff; font-family: 'Times New Roman', Georgia, serif; font-size: 26px; tracking: 0.15em; margin: 0; font-weight: normal; text-transform: uppercase;">ELEPHANT EXPEDITIONS</h1>
      <p style="color: #cda250; margin: 6px 0 0 0; font-size: 11px; font-weight: bold; letter-spacing: 0.25em; text-transform: uppercase;">Direct Contact Inquiry Channel</p>
    </div>

    <!-- Content Card -->
    <div style="padding: 36px; color: #2d2a23;">
      <h3 style="font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: 1.5px; color: #0f2c24; margin: 0 0 12px 0; border-left: 2px solid #cda250; padding-left: 10px;">
        1. Contact Information
      </h3>
      
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 28px; font-size: 13px;">
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c; width: 45%;">Name:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.name}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Email:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;"><a href="mailto:${data.email}" style="color: #b16a49; text-decoration: none;">${data.email}</a></td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Phone Line:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.phone || 'Not provided'}</td>
        </tr>
      </table>

      <h3 style="font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: 1.5px; color: #0f2c24; margin: 0 0 10px 0; border-left: 2px solid #cda250; padding-left: 10px;">
        2. Inquiry Message
      </h3>
      <div style="background-color: #f7f5f0; border-radius: 8px; padding: 16px; font-size: 12.5px; line-height: 1.6; color: #524f46; border: 1px solid #eae5db;">
        ${data.message.replace(/\n/g, '<br />')}
      </div>
    </div>

    <!-- Eco Footer Disclaimer -->
    <div style="background-color: #fcfbf9; padding: 24px; text-align: center; border-top: 1px solid #eae5db; font-size: 11px; color: #817a6c;">
      <p style="margin: 0; color: #cda250; font-weight: bold;">© ${new Date().getFullYear()} Elephant Expeditions. Direct Contact Services.</p>
    </div>
  </div>
</body>
</html>
    `;

		const plainTextBody = `
========================================
NEW DIRECT CONTACT INQUIRY
========================================
SENDER: ${data.name}
EMAIL: ${data.email}
PHONE: ${data.phone || 'Not provided'}

MESSAGE:
"${data.message}"

========================================
Elephant Expeditions © ${new Date().getFullYear()}
    `;

		try {
			if (endpointUrl) {
				const response = await fetch(endpointUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						leadName: data.name,
						leadEmail: data.email,
						subject: `[Contact Form] ${data.name}`,
						html: htmlEmailTemplate,
						text: plainTextBody,
					}),
				});

				if (!response.ok) {
					throw new Error(`Server returned error status ${response.status}`);
				}

				setStatus('success');
				setLoading(false);
				return {
					success: true,
					message: 'Thank you. Your message has been transmitted successfully to Peter.',
					formattedHtml: htmlEmailTemplate,
				};
			} else {
				await new Promise(resolve => setTimeout(resolve, 1500));
				setStatus('success');
				setLoading(false);
				return {
					success: true,
					message: 'Thank you. Your message has been transmitted successfully.',
					formattedHtml: htmlEmailTemplate,
				};
			}
		} catch (err: any) {
			console.error('Email transmission failure:', err);
			setError(err?.message || 'Standard network gateway error. Please try again.');
			setStatus('error');
			setLoading(false);
			return {
				success: false,
				message: 'Could not transmit message. Please try sending directly to info@elephant-expeditions.com',
			};
		}
	};

	const sendGroupTripWaitlist = async (data: GroupTripWaitlistData) => {
		setLoading(true);
		setStatus('sending');
		setError(null);

		// Honeypot anti-spam check
		if (data.website && data.website.trim()) {
			setStatus('success');
			setLoading(false);
			return {
				success: true,
				message: 'You have been registered for the VIP waitlist.',
			};
		}

		const tripName = data.trip || 'Group Trip — July 2027';

		const htmlEmailTemplate = `
<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <title>Elephant Expeditions - Group Trip VIP Waitlist</title>
</head>
<body style="font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif; background-color: #f7f5f0; margin: 0; padding: 40px 20px;">
  <div style="max-w: 600px; margin: 0 auto; background-color: #ffffff; border-radius: 16px; overflow: hidden; box-shadow: 0 4px 20px rgba(15, 44, 36, 0.08); border: 1px solid #e3decb;">
    <!-- Brand Header Banner -->
    <div style="background-color: #0f2c24; padding: 32px; text-align: center; border-bottom: 3px solid #cda250;">
      <h1 style="color: #ffffff; font-family: 'Times New Roman', Georgia, serif; font-size: 26px; tracking: 0.15em; margin: 0; font-weight: normal; text-transform: uppercase;">ELEPHANT EXPEDITIONS</h1>
      <p style="color: #cda250; margin: 6px 0 0 0; font-size: 11px; font-weight: bold; letter-spacing: 0.25em; text-transform: uppercase;">Exclusive Departure VIP Waitlist</p>
    </div>

    <!-- Content Card -->
    <div style="padding: 36px; color: #2d2a23;">
      <h3 style="font-size: 12px; font-weight: bold; text-transform: uppercase; letter-spacing: 1.5px; color: #0f2c24; margin: 0 0 12px 0; border-left: 2px solid #cda250; padding-left: 10px;">
        1. Waitlist Registration
      </h3>
      
      <table style="width: 100%; border-collapse: collapse; margin-bottom: 28px; font-size: 13px;">
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c; width: 45%;">Target Expedition:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${tripName}</td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Registered Email:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;"><a href="mailto:${data.email}" style="color: #b16a49; text-decoration: none;">${data.email}</a></td>
        </tr>
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Registration Date:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}</td>
        </tr>
        ${
					data.language
						? `
        <tr style="border-bottom: 1px solid #f2ede4;">
          <td style="padding: 10px 0; color: #817a6c;">Preferred Language:</td>
          <td style="padding: 10px 0; font-weight: bold; color: #0f2c24;">${data.language.toUpperCase()}</td>
        </tr>`
						: ''
				}
      </table>

      <div style="background-color: #f7f5f0; border-radius: 8px; padding: 16px; font-size: 12.5px; line-height: 1.6; color: #524f46; border: 1px solid #eae5db;">
        A new guest has requested priority notification and early itinerary access for the <strong>${tripName}</strong>. Please ensure they receive the complete schedule and priority booking window as soon as allocations open.
      </div>
    </div>

    <!-- Eco Footer Disclaimer -->
    <div style="background-color: #fcfbf9; padding: 24px; text-align: center; border-top: 1px solid #eae5db; font-size: 11px; color: #817a6c;">
      <p style="margin: 0; color: #cda250; font-weight: bold;">© ${new Date().getFullYear()} Elephant Expeditions. Group Departures Register.</p>
    </div>
  </div>
</body>
</html>
		`;

		const plainTextBody = `
========================================
NEW GROUP TRIP VIP WAITLIST REGISTRATION
========================================
EXPEDITION: ${tripName}
EMAIL: ${data.email}
DATE: ${new Date().toISOString()}
LANGUAGE: ${data.language || 'en'}

The guest has joined the priority notification list for the July 2027 Group Trip.
========================================
Elephant Expeditions © ${new Date().getFullYear()}
		`;

		try {
			if (endpointUrl) {
				const response = await fetch(endpointUrl, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
					},
					body: JSON.stringify({
						leadEmail: data.email,
						subject: `[VIP Waitlist] ${tripName} - ${data.email}`,
						html: htmlEmailTemplate,
						text: plainTextBody,
						// details: {
						// 	trip: tripName,
						// 	email: data.email,
						// 	language: data.language,
						// },
					}),
				});

				if (!response.ok) {
					console.error(await response.text());
					throw new Error(`Server returned error status ${response.status}`);
				}

				setStatus('success');
				setLoading(false);
				return {
					success: true,
					message: 'You have been registered for the VIP waitlist.',
					formattedHtml: htmlEmailTemplate,
				};
			} else {
				await new Promise(resolve => setTimeout(resolve, 1500));
				setStatus('success');
				setLoading(false);
				return {
					success: true,
					message: 'You have been registered for the VIP waitlist.',
					formattedHtml: htmlEmailTemplate,
				};
			}
		} catch (err: any) {
			console.error('Email transmission failure:', err);
			setError(err?.message || 'Standard network gateway error. Please try again.');
			setStatus('error');
			setLoading(false);
			return {
				success: false,
				message: 'Could not transmit registration. Please try again or email concierge@elephant-expeditions.com',
			};
		}
	};

	return {
		loading,
		status,
		error,
		sendItineraryProposal,
		sendContactInquiry,
		sendGroupTripWaitlist,
	};
}
